import { useState } from 'react'
import {
  Stack,
  Title,
  Button,
  Group,
  TextInput,
  Table,
  Badge,
  ActionIcon,
  Modal,
  Text,
  Card,
  Grid,
  Select,
  Checkbox,
  NumberInput,
  Alert,
  Code,
  Collapse,
} from '@mantine/core'
import {
  IconPlus,
  IconSearch,
  IconTrash,
  IconInfoCircle,
  IconAlertCircle,
  IconNetwork,
  IconLock,
  IconChevronDown,
  IconChevronUp,
} from '@tabler/icons-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useDisclosure } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'
import { networksApi } from '@/api'
import type { Network, CreateNetworkRequest } from '@/types'

export function Networks() {
  const [search, setSearch] = useState('')
  const [createOpened, { open: openCreate, close: closeCreate }] = useDisclosure(false)
  const [deleteOpened, { open: openDelete, close: closeDelete }] = useDisclosure(false)
  const [selectedNetwork, setSelectedNetwork] = useState<Network | null>(null)
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())
  const queryClient = useQueryClient()

  const { data: networks = [], isLoading } = useQuery({
    queryKey: ['networks'],
    queryFn: async () => {
      const { data } = await networksApi.list()
      return data
    },
    refetchInterval: 10000,
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => networksApi.delete(id),
    onSuccess: () => {
      notifications.show({
        title: 'Success',
        message: 'Network deleted successfully',
        color: 'green',
      })
      queryClient.invalidateQueries({ queryKey: ['networks'] })
      closeDelete()
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to delete network',
        color: 'red',
      })
    },
  })

  const filteredNetworks = networks.filter((network) =>
    network.Name.toLowerCase().includes(search.toLowerCase())
  )

  const handleDeleteClick = (network: Network) => {
    setSelectedNetwork(network)
    openDelete()
  }

  const handleDeleteConfirm = () => {
    if (selectedNetwork) {
      deleteMutation.mutate(selectedNetwork.Id)
    }
  }

  const toggleRow = (networkId: string) => {
    const newExpanded = new Set(expandedRows)
    if (newExpanded.has(networkId)) {
      newExpanded.delete(networkId)
    } else {
      newExpanded.add(networkId)
    }
    setExpandedRows(newExpanded)
  }

  const getDriverBadgeColor = (driver: string) => {
    switch (driver.toLowerCase()) {
      case 'overlay':
        return 'blue'
      case 'bridge':
        return 'green'
      case 'host':
        return 'orange'
      default:
        return 'gray'
    }
  }

  const getScopeBadgeColor = (scope: string) => {
    return scope === 'swarm' ? 'violet' : 'gray'
  }

  const getContainerCount = (network: Network) => {
    if (!network.Containers) return 0
    return Object.keys(network.Containers).length
  }

  const getVxlanVni = (network: Network) => {
    // Extract VNI from options if available
    return network.Options?.['com.rustswarm.network.vxlan.vni'] || 'N/A'
  }

  const rows = filteredNetworks.map((network) => {
    const isExpanded = expandedRows.has(network.Id)
    const containerCount = getContainerCount(network)
    const subnet = network.IPAM.Config[0]?.Subnet || 'N/A'
    const gateway = network.IPAM.Config[0]?.Gateway || 'N/A'
    const vni = getVxlanVni(network)

    return (
      <>
        <Table.Tr key={network.Id}>
          <Table.Td>
            <Group gap="xs">
              <ActionIcon
                variant="subtle"
                size="sm"
                onClick={() => toggleRow(network.Id)}
              >
                {isExpanded ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
              </ActionIcon>
              <Text fw={500}>{network.Name}</Text>
              {network.Internal && (
                <Badge size="xs" variant="light" color="yellow">
                  Internal
                </Badge>
              )}
              {network.Labels?.encrypted === 'true' && (
                <IconLock size={14} color="var(--mantine-color-blue-6)" />
              )}
            </Group>
          </Table.Td>
          <Table.Td>
            <Badge color={getDriverBadgeColor(network.Driver)} variant="light">
              {network.Driver}
            </Badge>
          </Table.Td>
          <Table.Td>
            <Badge color={getScopeBadgeColor(network.Scope)} variant="dot">
              {network.Scope}
            </Badge>
          </Table.Td>
          <Table.Td>
            <Text size="sm" c="dimmed">
              {subnet}
            </Text>
          </Table.Td>
          <Table.Td>
            <Text size="sm" c="dimmed">
              {gateway}
            </Text>
          </Table.Td>
          <Table.Td>
            <Text size="sm" c="dimmed">
              {network.Driver === 'overlay' ? vni : 'N/A'}
            </Text>
          </Table.Td>
          <Table.Td>
            <Badge variant="outline">{containerCount}</Badge>
          </Table.Td>
          <Table.Td>
            <Group gap="xs" justify="flex-end">
              <ActionIcon
                variant="subtle"
                color="red"
                onClick={() => handleDeleteClick(network)}
                title="Delete network"
              >
                <IconTrash size={16} />
              </ActionIcon>
            </Group>
          </Table.Td>
        </Table.Tr>
        {isExpanded && (
          <Table.Tr key={`${network.Id}-details`}>
            <Table.Td colSpan={8}>
              <Card withBorder p="md" bg="gray.0">
                <Stack gap="md">
                  <Group>
                    <Text fw={600}>Network ID:</Text>
                    <Code>{network.Id}</Code>
                  </Group>

                  <Group>
                    <Text fw={600}>Created:</Text>
                    <Text size="sm">
                      {new Date(network.Created).toLocaleString()}
                    </Text>
                  </Group>

                  {network.IPAM.Config[0]?.IPRange && (
                    <Group>
                      <Text fw={600}>IP Range:</Text>
                      <Code>{network.IPAM.Config[0].IPRange}</Code>
                    </Group>
                  )}

                  {Object.keys(network.Labels).length > 0 && (
                    <div>
                      <Text fw={600} mb="xs">
                        Labels:
                      </Text>
                      <Stack gap="xs">
                        {Object.entries(network.Labels).map(([key, value]) => (
                          <Group key={key} gap="xs">
                            <Code>{key}</Code>
                            <Text size="sm">=</Text>
                            <Code>{value}</Code>
                          </Group>
                        ))}
                      </Stack>
                    </div>
                  )}

                  {network.Containers && Object.keys(network.Containers).length > 0 && (
                    <div>
                      <Text fw={600} mb="xs">
                        Connected Containers ({containerCount}):
                      </Text>
                      <Stack gap="xs">
                        {Object.entries(network.Containers).map(([id, container]) => (
                          <Group key={id} gap="xs">
                            <Text size="sm" fw={500}>
                              {container.Name}
                            </Text>
                            <Text size="sm" c="dimmed">
                              {container.IPv4Address}
                            </Text>
                          </Group>
                        ))}
                      </Stack>
                    </div>
                  )}

                  {network.Driver === 'overlay' && (
                    <Alert
                      icon={<IconInfoCircle size={16} />}
                      title="VXLAN Overlay Network"
                      color="blue"
                    >
                      <Text size="sm">
                        This overlay network uses VXLAN for multi-host container communication.
                        VNI: {vni}, Port: 4789, MTU: 1450
                      </Text>
                    </Alert>
                  )}
                </Stack>
              </Card>
            </Table.Td>
          </Table.Tr>
        )}
      </>
    )
  })

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Title order={2}>Networks</Title>
        <Button leftSection={<IconPlus size={18} />} onClick={openCreate}>
          Create Network
        </Button>
      </Group>

      <TextInput
        placeholder="Search networks..."
        leftSection={<IconSearch size={18} />}
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
      />

      <Card withBorder>
        <Table horizontalSpacing="md" verticalSpacing="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Name</Table.Th>
              <Table.Th>Driver</Table.Th>
              <Table.Th>Scope</Table.Th>
              <Table.Th>Subnet</Table.Th>
              <Table.Th>Gateway</Table.Th>
              <Table.Th>VXLAN VNI</Table.Th>
              <Table.Th>Containers</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {isLoading ? (
              <Table.Tr>
                <Table.Td colSpan={8}>
                  <Text ta="center" c="dimmed">
                    Loading networks...
                  </Text>
                </Table.Td>
              </Table.Tr>
            ) : rows.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={8}>
                  <Text ta="center" c="dimmed">
                    No networks found
                  </Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              rows
            )}
          </Table.Tbody>
        </Table>
      </Card>

      <CreateNetworkModal
        opened={createOpened}
        onClose={closeCreate}
        onSuccess={() => {
          closeCreate()
          queryClient.invalidateQueries({ queryKey: ['networks'] })
        }}
      />

      <Modal
        opened={deleteOpened}
        onClose={closeDelete}
        title="Delete Network"
        size="md"
      >
        <Stack gap="md">
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Warning"
            color="red"
          >
            Are you sure you want to delete the network{' '}
            <strong>{selectedNetwork?.Name}</strong>? This action cannot be undone.
            {selectedNetwork && getContainerCount(selectedNetwork) > 0 && (
              <>
                <br />
                <br />
                This network has {getContainerCount(selectedNetwork)} connected
                container(s). You must disconnect all containers before deleting the
                network.
              </>
            )}
          </Alert>

          <Group justify="flex-end">
            <Button variant="default" onClick={closeDelete}>
              Cancel
            </Button>
            <Button
              color="red"
              onClick={handleDeleteConfirm}
              loading={deleteMutation.isPending}
              disabled={
                selectedNetwork ? getContainerCount(selectedNetwork) > 0 : false
              }
            >
              Delete Network
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  )
}

interface CreateNetworkModalProps {
  opened: boolean
  onClose: () => void
  onSuccess: () => void
}

function CreateNetworkModal({ opened, onClose, onSuccess }: CreateNetworkModalProps) {
  const [name, setName] = useState('')
  const [driver, setDriver] = useState<string>('overlay')
  const [subnet, setSubnet] = useState('')
  const [gateway, setGateway] = useState('')
  const [internal, setInternal] = useState(false)
  const [attachable, setAttachable] = useState(true)
  const [encrypted, setEncrypted] = useState(false)

  const createMutation = useMutation({
    mutationFn: (network: CreateNetworkRequest) => networksApi.create(network),
    onSuccess: () => {
      notifications.show({
        title: 'Success',
        message: 'Network created successfully',
        color: 'green',
      })
      resetForm()
      onSuccess()
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to create network',
        color: 'red',
      })
    },
  })

  const resetForm = () => {
    setName('')
    setDriver('overlay')
    setSubnet('')
    setGateway('')
    setInternal(false)
    setAttachable(true)
    setEncrypted(false)
  }

  const handleSubmit = () => {
    if (!name) {
      notifications.show({
        title: 'Validation Error',
        message: 'Network name is required',
        color: 'red',
      })
      return
    }

    const network: CreateNetworkRequest = {
      Name: name,
      Driver: driver,
      Internal: internal,
      Attachable: attachable,
      Labels: encrypted ? { encrypted: 'true' } : {},
    }

    if (subnet) {
      network.IPAM = {
        Driver: 'default',
        Config: [
          {
            Subnet: subnet,
            Gateway: gateway || undefined,
          },
        ],
      }
    }

    createMutation.mutate(network)
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Create Network"
      size="lg"
    >
      <Stack gap="md">
        <TextInput
          label="Network Name"
          placeholder="my-network"
          required
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          description="Unique name for the network"
        />

        <Select
          label="Driver"
          required
          value={driver}
          onChange={(value) => setDriver(value || 'overlay')}
          data={[
            { value: 'overlay', label: 'Overlay (VXLAN multi-host)' },
            { value: 'bridge', label: 'Bridge (single-host)' },
            { value: 'host', label: 'Host (no isolation)' },
          ]}
          description="Network driver type"
        />

        <TextInput
          label="Subnet (CIDR)"
          placeholder="10.0.0.0/24"
          value={subnet}
          onChange={(e) => setSubnet(e.currentTarget.value)}
          description="Network subnet in CIDR notation (optional)"
        />

        <TextInput
          label="Gateway"
          placeholder="10.0.0.1"
          value={gateway}
          onChange={(e) => setGateway(e.currentTarget.value)}
          description="Gateway IP address (optional)"
          disabled={!subnet}
        />

        <Checkbox
          label="Internal network"
          checked={internal}
          onChange={(e) => setInternal(e.currentTarget.checked)}
          description="Restrict external access to this network"
        />

        <Checkbox
          label="Attachable"
          checked={attachable}
          onChange={(e) => setAttachable(e.currentTarget.checked)}
          description="Allow containers to attach to this network"
        />

        {driver === 'overlay' && (
          <Checkbox
            label="Enable encryption"
            checked={encrypted}
            onChange={(e) => setEncrypted(e.currentTarget.checked)}
            description="Encrypt VXLAN traffic (IPsec)"
          />
        )}

        {driver === 'overlay' && (
          <Alert
            icon={<IconNetwork size={16} />}
            title="VXLAN Overlay Network"
            color="blue"
          >
            <Text size="sm">
              Overlay networks use VXLAN to enable communication between containers
              across multiple hosts. Port 4789/UDP must be open for VXLAN traffic.
            </Text>
          </Alert>
        )}

        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={createMutation.isPending}>
            Create Network
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
